if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        document.querySelector(".product-form__input--pill input:checked")?.removeAttribute("checked");
        this.quantityInput = this.querySelector('.quantity__input');
      }

      getPreOrderData(variantId) {
        const productFormData = this.querySelector('#product-form-data') || document.querySelector('#product-form-data');

        if (!productFormData || !variantId) {
          return {
            isPreOrder: false,
            isPreOrderEnabled: false,
            isPreOrderLimitReached: false,
            limit: null,
            inventoryQuantity: null,
            message: '',
          };
        }

        const option = Array.from(productFormData.options).find((option) => option.value === String(variantId));

        if (!option) {
          return {
            isPreOrder: false,
            isPreOrderEnabled: false,
            isPreOrderLimitReached: false,
            limit: null,
            inventoryQuantity: null,
            message: '',
          };
        }

        productFormData.value = String(variantId);

        const isPreOrderEnabled = option.dataset.preorder === 'true';
        const limit = option.dataset.preorderLimit ? parseInt(option.dataset.preorderLimit, 10) : null;
        const inventoryQuantity = option.dataset.inventoryQuantity ? parseInt(option.dataset.inventoryQuantity, 10) : 0;

        const isPreOrderLimitReached =
          isPreOrderEnabled &&
          Number.isInteger(limit) &&
          limit > 0 &&
          inventoryQuantity <= -limit;

        return {
          isPreOrder: isPreOrderEnabled && !isPreOrderLimitReached,
          isPreOrderEnabled,
          isPreOrderLimitReached,
          limit,
          inventoryQuantity,
          message: option.dataset.preorderMessage || '',
        };
      }

      updatePreOrderMessage(variantId) {
		  const preOrderData = this.getPreOrderData(variantId);

		  const messageWrapper =
		    this.querySelector('.pre-order-message') ||
		    document.querySelector('.pre-order-message');

		  const messageText = messageWrapper?.querySelector('span');

		  if (!messageWrapper || !messageText) return;

		  if (preOrderData.isPreOrder && preOrderData.message) {
		    messageText.textContent = preOrderData.message;
		    messageWrapper.classList.remove('hidden');
		    messageWrapper.classList.add('active');

		    this.updatePaymentDeliveryNote();
		    return;
		  }

		  messageText.textContent = '';
		  messageWrapper.classList.add('hidden');
		  messageWrapper.classList.remove('active');

		  this.updatePaymentDeliveryNote();
		}

		updatePaymentDeliveryNote() {
		  const messageWrapper =
		    this.querySelector('.pre-order-message') ||
		    document.querySelector('.pre-order-message');

		  const paymentDeliveryNote =
		    this.querySelector('.block__payment-delivery-note') ||
		    document.querySelector('.block__payment-delivery-note');

		  if (!paymentDeliveryNote) return;

		  const hasVisibleMessage =
		    messageWrapper &&
		    messageWrapper.classList.contains('active') &&
		    !messageWrapper.classList.contains('hidden') &&
		    messageWrapper.querySelector('span')?.textContent.trim();

		  paymentDeliveryNote.classList.toggle('hidden', Boolean(hasVisibleMessage));
		}

      updatePreOrderProperty(variantId) {
		  const currentForm = this.querySelector(`#product-form-${this.dataset.section}`);
		  if (!currentForm) return;

		  const preOrderData = this.getPreOrderData(variantId);
		  const existingInput = currentForm.querySelector('[data-pre-order-property]');

		  if (preOrderData.isPreOrder) {
		    if (!existingInput) {
		      const input = document.createElement('input');
		      input.type = 'hidden';
		      input.name = 'properties[Pre-Order]';
		      input.value = preOrderData.message || 'Pre-order';
		      input.setAttribute('data-pre-order-property', '');

		      currentForm.appendChild(input);
		    } else {
		      existingInput.value = preOrderData.message || 'Pre-order';
		    }

		    return;
		  }

		  existingInput?.remove();
		}

		connectedCallback() {
		  this.initializeProductSwapUtility();

		  this.onVariantChangeUnsubscriber = subscribe(
		    PUB_SUB_EVENTS.optionValueSelectionChange,
		    this.handleOptionValueChange.bind(this)
		  );

		  this.initQuantityHandlers();

		  requestAnimationFrame(() => {
		    this.updatePaymentDeliveryNote();
		  });

		  this.dispatchEvent(
		    new CustomEvent('product-info:loaded', {
		      bubbles: true,
		    })
		  );
		}

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

     updatePreOrderButton(variantId, html) {
		  const currentButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
		  const currentButtonText = currentButton?.querySelector('span');

		  const updatedButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
		  const updatedButtonText = updatedButton?.querySelector('span');

		  if (!currentButton || !currentButtonText || !updatedButton || !updatedButtonText) return;

		  const preOrderData = this.getPreOrderData(variantId);
		  const isPreOrder = preOrderData.isPreOrder;
		  const isPreOrderLimitReached = preOrderData.isPreOrderLimitReached;
		  const isDisabled = updatedButton.hasAttribute('disabled');

		  currentButton.classList.toggle('pre-order', isPreOrder);

		  if (isPreOrderLimitReached) {
		    currentButton.classList.remove('pre-order');
		    currentButton.setAttribute('disabled', 'disabled');
		    currentButton.setAttribute('aria-disabled', 'true');

		    currentButtonText.textContent = window.variantStrings.soldOut;
		    currentButtonText.dataset.text = window.variantStrings.soldOut;

		    return;
		  }

		  if (isPreOrder) {
		    const preOrderText = window.variantStrings.preOrder;

		    currentButton.removeAttribute('disabled');
		    currentButton.removeAttribute('aria-disabled');

		    currentButtonText.innerHTML = `
		      <svg
		        width="24"
		        height="24"
		        viewBox="0 0 24 24"
		        fill="none"
		        xmlns="http://www.w3.org/2000/svg"
		        aria-hidden="true"
		        focusable="false"
		      >
		        <rect width="24" height="24" fill="transparent"/>
		        <circle
		          cx="12"
		          cy="12"
		          r="9"
		          stroke="currentColor"
		          stroke-linecap="round"
		          stroke-linejoin="round"
		        />
		        <path
		          d="M12 6V12L7.5 7.5"
		          stroke="currentColor"
		          stroke-linecap="round"
		          stroke-linejoin="round"
		        />
		      </svg>
		      <span class="product-form__submit-label">${preOrderText}</span>
		    `;

		    currentButtonText.dataset.text = preOrderText;

		    return;
		  }

		  currentButton.classList.remove('pre-order');

		  if (isDisabled) {
		    currentButton.setAttribute('disabled', 'disabled');
		    currentButton.setAttribute('aria-disabled', 'true');
		  } else {
		    currentButton.removeAttribute('disabled');
		    currentButton.removeAttribute('aria-disabled');
		  }

		  const updatedText = updatedButtonText.textContent.trim();

		  currentButtonText.textContent = updatedText;
		  currentButtonText.dataset.text = updatedText;
		}

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updatePreOrderButton(variant.id, html);
          this.updatePreOrderProperty(variant.id);
          this.updatePreOrderMessage(variant.id);

          const btn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

          const stock_label = btn?.hasAttribute('disabled') ? 'Item Out of Stock' : 'Item in Stock';
          if (document.querySelector('.block__delivery-in-stock') != null) {
            document.querySelector('.block__delivery-in-stock').textContent = stock_label;
          }

          document.querySelector('.block__variant-size-select-msg')?.classList.add('hidden');

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);

              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;

            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');

              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);

                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);

                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}